import React, { ReactNode, useState } from 'react';
import styled from 'styled-components';

import { CsvImportFileRow } from '@/features/csvImport/admin/atoms/CsvImportFileRow';
import { CsvImportSummaryCard } from '@/features/csvImport/admin/atoms/CsvImportSummaryCard';
import { CsvImportBucketSection } from '@/features/csvImport/admin/molecules/CsvImportBucketSection';
import { CsvImportBucket } from '@/features/csvImport/admin/types/csvImport';
import { Button } from '@/ui/Button';

const RootStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TitleStyled = styled.h1`
  ${(p) => p.theme.typography.title2};
  /* The legacy global stylesheet still puts em-based margins on headings and paragraphs */
  margin: 0;
  color: ${(p) => p.theme.colors.neutral.onBackground};
`;

const SummaryStyled = styled.div`
  display: flex;
  flex-direction: column;
  /* Keeps the trailing action hugging the left edge instead of stretching across the row */
  align-items: flex-start;
  gap: 8px;
`;

const SUMMARY_CARDS_GAP = 10;

const SummaryCardsStyled = styled.div`
  display: flex;
  align-self: stretch;
  align-items: stretch;
  gap: ${SUMMARY_CARDS_GAP}px;

  /* Cards split the row evenly, so a lone one would stretch across it. Hold it to the width
     it would have next to a second card instead, which is the state the design shows. */
  > *:only-child {
    flex: 0 1 calc(50% - ${SUMMARY_CARDS_GAP / 2}px);
  }
`;

const SectionsStyled = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

/**
 * Which section is open, tied to the bucket set it was decided for — see the reset below.
 * A `null` id is a deliberate "everything collapsed", so it cannot double as "not decided yet".
 */
type BucketExpansion = {
  bucketKey: string;
  expandedBucketId: string | null;
};

type CsvImportReviewProps = {
  fileName: string;
  onUploadDifferentFile: VoidFunction;
  buckets: CsvImportBucket[];
  title?: string;
  /**
   * Whether the first bucket that has rows to show starts open. Off for a flow whose design
   * opens the review screen with every section collapsed.
   */
  expandFirstBucket?: boolean;
  /** Control under the summary cards, e.g. a "Download errors" link. */
  summaryAction?: ReactNode;
  /**
   * Anything the flow adds below the bucket sections — batch-wide settings, a notice about
   * the background job. Kept open-ended because the two bulk flows differ only here.
   */
  children?: ReactNode;
  className?: string;
};

/**
 * The review step both bulk-CSV flows share: what was uploaded, how the rows split across
 * buckets, and the rows themselves. Buckets holding no rows are dropped entirely — no
 * summary card and no section — and only one section stays expanded at a time.
 */
export const CsvImportReview = ({
  fileName,
  onUploadDifferentFile,
  buckets,
  title = 'Review your upload',
  expandFirstBucket = true,
  summaryAction,
  children,
  className,
}: CsvImportReviewProps): JSX.Element => {
  const visibleBuckets = buckets.filter(({ count }) => count > 0);
  const bucketKey = visibleBuckets.map(({ id }) => id).join('|');
  const defaultExpandedBucketId = expandFirstBucket
    ? (visibleBuckets.find(({ renderContent }) => !!renderContent)?.id ?? null)
    : null;

  const [expansion, setExpansion] = useState<BucketExpansion>({ bucketKey, expandedBucketId: defaultExpandedBucketId });

  // A different set of buckets means a different upload — or the first one arriving after an
  // empty render — so what was open before says nothing about what should be open now. Without
  // this, the remembered section can name a bucket that is gone and leave everything collapsed.
  if (expansion.bucketKey !== bucketKey) {
    setExpansion({ bucketKey, expandedBucketId: defaultExpandedBucketId });
  }

  const expandedBucketId = expansion.bucketKey === bucketKey ? expansion.expandedBucketId : defaultExpandedBucketId;

  const toggleBucket = (bucketId: string) =>
    setExpansion({ bucketKey, expandedBucketId: expandedBucketId === bucketId ? null : bucketId });

  return (
    <RootStyled className={className}>
      <CsvImportFileRow
        fileName={fileName}
        action={
          <Button variant="link" onClick={onUploadDifferentFile}>
            Upload a different file
          </Button>
        }
      />
      <TitleStyled>{title}</TitleStyled>
      <SummaryStyled>
        <SummaryCardsStyled>
          {visibleBuckets.map(({ id, icon, count, summaryLabel, variant }) => (
            <CsvImportSummaryCard key={id} icon={icon} count={count} label={summaryLabel} variant={variant} />
          ))}
        </SummaryCardsStyled>
        {summaryAction}
      </SummaryStyled>
      <SectionsStyled>
        {visibleBuckets.map(({ id, label, count, variant, renderContent }) => {
          const isExpanded = expandedBucketId === id;

          return (
            <CsvImportBucketSection
              key={id}
              label={label}
              count={count}
              variant={variant}
              isExpanded={isExpanded}
              onToggle={renderContent && (() => toggleBucket(id))}
            >
              {isExpanded && renderContent?.()}
            </CsvImportBucketSection>
          );
        })}
      </SectionsStyled>
      {children}
    </RootStyled>
  );
};
